import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMundusStore } from '../stores/mundus.store';
import {
  Moon,
  Star,
  TrendingUp,
  Award,
  MessageSquare,
  ChevronRight,
  Sparkles,
  Calendar,
  Loader2
} from 'lucide-react';
import { DailyReport } from '../types/mundus';
import { getTodaysReport, submitReportFeedback, markReportAsRead } from '../lib/reports.api';
import { getDailyPlan } from '../lib/plans.api';

export const EveningDebrief: React.FC = () => {
  const navigate = useNavigate();
  const { munduses } = useMundusStore();
  const [report, setReport] = useState<DailyReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userRating, setUserRating] = useState<number>(0);
  const [userFeedback, setUserFeedback] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tomorrowPlanExists, setTomorrowPlanExists] = useState(false);

  const activeMundus = munduses.find((m) => m.isActive) || munduses[0];

  useEffect(() => {
    if (activeMundus) {
      loadTodaysReport();
      checkTomorrowPlan();
    }
  }, [activeMundus]);

  const loadTodaysReport = async () => {
    if (!activeMundus) return;

    setIsLoading(true);
    try {
      const todaysReport = await getTodaysReport(activeMundus.id);
      if (todaysReport) {
        setReport(todaysReport);
        setUserRating(todaysReport.userRating || 0);
        setUserFeedback(todaysReport.userFeedback || '');

        // Mark as read if not already
        if (!todaysReport.isRead) {
          await markReportAsRead(todaysReport.id);
        }
      }
    } catch (error) {
      console.error('Error loading today\'s report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkTomorrowPlan = async () => {
    if (!activeMundus) return;

    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const plan = await getDailyPlan(activeMundus.id, tomorrow);
      setTomorrowPlanExists(!!plan);
    } catch (error) {
      setTomorrowPlanExists(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!report || userRating === 0) return;

    setIsSubmitting(true);
    try {
      const updatedReport = await submitReportFeedback(
        report.id,
        userRating,
        userFeedback || undefined
      );
      setReport(updatedReport);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 18) return 'Good evening';
    if (hour >= 12) return 'Good afternoon';
    return 'Hello';
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!activeMundus) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">No Active Mundus</h2>
          <p className="text-slate-600 mb-6">Create a Mundus to get started</p>
          <button
            onClick={() => navigate('/munduses')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
                <Moon className="w-8 h-8 text-indigo-600" />
                {getGreeting()}!
              </h1>
              <p className="text-slate-600 mt-1 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate()}
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
            >
              Back to Dashboard →
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Daily Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-indigo-600" />
                  Today's Summary
                </h2>
                {report?.overallScore !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-bold text-indigo-600">
                      {report.overallScore.toFixed(0)}
                    </span>
                    <span className="text-sm text-slate-600">/100</span>
                  </div>
                )}
              </div>

              {isLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
                  <p className="text-slate-600">Loading your report...</p>
                </div>
              ) : report ? (
                <div className="space-y-6">
                  {/* Summary Text */}
                  {report.summary && (
                    <div className="prose prose-slate max-w-none">
                      <p className="text-slate-700 whitespace-pre-wrap">{report.summary}</p>
                    </div>
                  )}

                  {/* Key Findings */}
                  {report.keyFindings && Array.isArray(report.keyFindings) && report.keyFindings.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        Key Findings
                      </h3>
                      <div className="space-y-3">
                        {report.keyFindings.map((finding: any, index: number) => (
                          <div
                            key={index}
                            className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                          >
                            {finding.metric && (
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-slate-900">{finding.metric}</span>
                                {finding.value !== undefined && (
                                  <span className="text-sm text-slate-600">
                                    {finding.value} {finding.unit || ''}
                                  </span>
                                )}
                              </div>
                            )}
                            {finding.progressPercent !== undefined && finding.progressPercent !== null && (
                              <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                                <div
                                  className="bg-green-600 h-2 rounded-full transition-all"
                                  style={{ width: `${Math.min(finding.progressPercent, 100)}%` }}
                                ></div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {report.recommendations && Array.isArray(report.recommendations) && report.recommendations.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Award className="w-5 h-5 text-blue-600" />
                        Recommendations
                      </h3>
                      <div className="space-y-2">
                        {report.recommendations.map((rec: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200"
                          >
                            <ChevronRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-slate-700">{rec.text || rec}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Feedback Section */}
                  <div className="pt-6 border-t border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-purple-600" />
                      Your Feedback
                    </h3>

                    {/* Rating */}
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">How was your day?</p>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setUserRating(star)}
                            className="transition-transform hover:scale-110"
                          >
                            <Star
                              className={`w-8 h-8 ${
                                star <= userRating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-slate-300'
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Feedback Text */}
                    <div className="mb-4">
                      <label className="block text-sm text-slate-600 mb-2">
                        Any thoughts or reflections? (Optional)
                      </label>
                      <textarea
                        value={userFeedback}
                        onChange={(e) => setUserFeedback(e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                        rows={4}
                        placeholder="What went well? What could be improved?"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      onClick={handleSubmitFeedback}
                      disabled={userRating === 0 || isSubmitting}
                      className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Feedback'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Moon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Report Yet</h3>
                  <p className="text-slate-600 mb-6">
                    Generate a daily report to see your progress and insights
                  </p>
                  <button
                    onClick={() => navigate(`/mundus/${activeMundus.id}`)}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                  >
                    Go to Mundus
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tomorrow's Plan */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Tomorrow
              </h3>
              {tomorrowPlanExists ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">
                    Your plan for tomorrow is ready to review
                  </p>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Preview Tomorrow
                  </button>
                </div>
              ) : (
                <p className="text-sm text-slate-600">
                  Tomorrow's plan will be generated soon
                </p>
              )}
            </div>

            {/* Achievements This Week */}
            {activeMundus.achievements && activeMundus.achievements.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-600" />
                  Recent Achievements
                </h3>
                <div className="space-y-3">
                  {activeMundus.achievements.slice(0, 3).map((achievement) => (
                    <div key={achievement.id} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                        <Award className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {achievement.title}
                        </p>
                        <p className="text-xs text-slate-500">{achievement.points} points</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate(`/mundus/${activeMundus.id}/achievements`)}
                  className="mt-4 w-full text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  View All Achievements →
                </button>
              </div>
            )}

            {/* Quick Stats */}
            {report && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Stats</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Overall Score</span>
                    <span className="text-sm font-semibold text-slate-900">
                      {report.overallScore?.toFixed(0) || 0}/100
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Agent Reports</span>
                    <span className="text-sm font-semibold text-slate-900">
                      {report.agentReports?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
